import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { Division } from "@/lib/types/deck-builder";
import { getDivisionImagePath, getCoalitionFlagPath, getCountryFlagPath } from "@/lib/utils/division-images";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

interface DivisionPickerProps {
  choices: string[];
  divisions: Division[];
  onDivisionChosen: (choice: number) => void;
  disabled?: boolean;
}

export function DivisionPicker({
  choices,
  divisions,
  onDivisionChosen,
  disabled = false,
}: DivisionPickerProps) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);

  const handleChoose = (index: number) => {
    if (disabled || isAnimating) return;
    
    setSelectedIndex(index);
    setIsAnimating(true);
    
    // Delay the callback to allow animation to play
    setTimeout(() => {
      onDivisionChosen(index);
    }, 300);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="mb-8 text-center"
      >
        <h2 className="text-3xl font-bold">Choose a Division</h2>
      </motion.div>

      <div className="flex flex-wrap justify-center gap-6 max-w-6xl mx-auto">
        <AnimatePresence mode="wait">
          {choices.map((divisionDescriptor, index) => {
            const division = divisions.find(
              (div) => div.descriptor === divisionDescriptor
            );

            if (!division) return null;

            const isSelected = selectedIndex === index;
            const shouldHide = selectedIndex !== null && selectedIndex !== index;

            return (
              <motion.div
                key={divisionDescriptor}
                initial={{ opacity: 0, y: 20 }}
                animate={{
                  opacity: shouldHide ? 0 : 1,
                  scale: isSelected ? 1.02 : 1,
                  y: 0,
                }}
                exit={{ opacity: 0 }}
                transition={{
                  duration: 0.3,
                  delay: index * 0.05,
                }}
                className="flex-1 min-w-[280px] max-w-[380px]"
              >
                <Card
                  className={`transition-all duration-200 ${
                    isSelected
                      ? "ring-2 ring-primary"
                      : "hover:border-primary/50"
                  } ${shouldHide ? "pointer-events-none" : ""}`}
                >
                  <CardContent className="p-6 flex flex-col items-center">
                    {/* Division Image */}
                    <div className="mb-4 relative">
                      <img
                        src={getDivisionImagePath(division.descriptor)}
                        alt={division.name}
                        className="w-40 h-40 object-contain"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = "/images/divisions/placeholder.png";
                        }}
                      />
                    </div>

                    {/* Division Name */}
                    <h3 className="text-xl font-semibold text-center mb-4">
                      {division.name}
                    </h3>

                    {/* Flags */}
                    <div className="flex gap-3 mb-4">
                      <img
                        src={getCoalitionFlagPath(division.alliance)}
                        alt={division.alliance}
                        className="w-6 h-6 object-contain"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = "none";
                        }}
                      />
                      <img
                        src={getCountryFlagPath(division.country)}
                        alt={division.country}
                        className="w-6 h-6 object-contain"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = "none";
                        }}
                      />
                    </div>

                    {/* Select Button */}
                    {!disabled && !shouldHide && (
                      <Button
                        onClick={() => handleChoose(index)}
                        disabled={isAnimating}
                        className="w-full"
                        size="lg"
                      >
                        Select
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
}
